import { useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

interface ClickableMetricProps {
  value: React.ReactNode;
  label?: string;
  context?: string;
  calculation?: string;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  showTooltip?: boolean;
  /** Color for the hover underline. Defaults to var(--accent). Pass the same
   * color used for the metric's value text (e.g. a Tier/Accuracy/Velocity
   * status color) so the hover state matches instead of always flashing teal. */
  hoverColor?: string;
}

export function ClickableMetric({
  value,
  label,
  context,
  calculation,
  onClick,
  className = "",
  style = {},
  showTooltip = true,
  hoverColor = "var(--accent)",
}: ClickableMetricProps) {
  const [isHovered, setIsHovered] = useState(false);

  const tooltipContent = calculation || (label && `Click to explore ${label} with AI`) || "Click to explore with AI";

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const metricElement = (
    <span
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`transition-smooth ${className}`}
      style={{
        cursor: 'pointer',
        borderBottom: isHovered ? `2px dashed ${hoverColor}` : '2px dashed transparent',
        paddingBottom: '2px',
        display: 'inline-block',
        ...style,
      }}
    >
      {value}
    </span>
  );

  if (!showTooltip) {
    return metricElement;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {metricElement}
      </TooltipTrigger>
      <TooltipContent 
        side="top" 
        className="max-w-xs"
        style={{
          backgroundColor: 'var(--popover)',
          color: 'var(--popover-foreground)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--elevation-md)',
        }}
      >
        <div className="space-y-1">
          {calculation && (
            <p style={{ fontSize: 'var(--text-sm)' }}>{calculation}</p>
          )}
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
            Click to explore with AI
          </p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}